import { useState } from 'react';

import { Stack } from '@mui/system';
import { Alert, Switch, Typography, FormControlLabel } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { TaskLinkedEntityType } from 'src/services/tasks/tasks.types';
import PostCommentForm from 'src/layouts/common/threads/post-comment-form';
import PostCommentList from 'src/layouts/common/threads/post-comment-list';

import LinkedTasks from 'src/sections/tasks/components/linked-tasks';

// ----------------------------------------------------------------------

type Props = {
  applicationId: string;
  internalThreadId?: string;
};

/**
 * An application's internal notes: the composer plus the merged feed. Shared by the application
 * page's Notes card and the Notes tab of both application quick views, so all three read and write
 * the same thread the same way.
 */
export default function ApplicationNotes({ applicationId, internalThreadId }: Props) {
  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  // Merged by default: the point of the feed is that the candidate's history is there without
  // anyone having to go looking for it.
  const [showProfileNotes, setShowProfileNotes] = useState<boolean>(true);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(`applications.detail.candidate_profile.internal_thread.subheader_${tenantType}`)}
      </Alert>

      {/* Written to this application's thread either way — the toggle only changes what is
          read back. Off, the list is the application's own notes and nothing else. */}
      <PostCommentForm thread_id={internalThreadId} />

      <LinkedTasks entityType={TaskLinkedEntityType.JOB_APPLICATION} entityId={applicationId} />

      <Stack direction="row" justifyContent="flex-end">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showProfileNotes}
              onChange={(_event, checked) => setShowProfileNotes(checked)}
            />
          }
          label={
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('applications.detail.candidate_profile.internal_thread.include_profile_notes')}
            </Typography>
          }
        />
      </Stack>

      {showProfileNotes
        ? <PostCommentList feed={{ type: 'application', id: applicationId }} />
        : <PostCommentList thread_id={internalThreadId} />}
    </>
  );
}
